import React, { useEffect, useMemo } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  ListGroup,
  ListGroupItem,
  Row
} from "reactstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getInvoicesList as onGetInvoicesList } from "../../store/invoices/actions";
import { getRequisitionList as onGetRequisitionList } from "../../store/requisitions/actions";
import { formatCurrency } from "../../common/Utils";
import { useActiveCityhall } from "../../context/ActiveCityhallContext";

import { withTranslation } from "react-i18next";
const Dashboard = props => {

  document.title = "Dashboard | Platform";

  const dispatch = useDispatch();
  const { activeCityhall } = useActiveCityhall();

  const { invoiceList, requisitionList, totalRequisitions } = useSelector(state => ({
    invoiceList: state.Invoices.invoiceList,
    requisitionList: state.Requisitions.requisitionList,
    totalRequisitions: state.Requisitions.totalItems
  }));

  useEffect(() => {
    dispatch(onGetInvoicesList());
    dispatch(onGetRequisitionList({ page: 1, pageSize: 5 }));
  }, [dispatch]);

  const invoiceStats = useMemo(() => {
    const invoices = Array.isArray(invoiceList) ? invoiceList : [];
    const total = invoices.length;
    const vigente = invoices.filter(inv => inv && inv.status === "Vigente").length;
    const conRequisicion = invoices.filter(inv => Boolean(inv && inv.requisition_id)).length;
    const sinRequisicion = invoices.filter(
      inv => inv && !inv.requisition_id && inv.status === "Vigente"
    );
    const sinRequisicionCount = sinRequisicion.length;
    const sinRequisicionMonto = sinRequisicion.reduce((acc, inv) => {
      const totalInv = Number.parseFloat(inv && inv.total ? inv.total : 0);
      return acc + (Number.isFinite(totalInv) ? totalInv : 0);
    }, 0);

    const noVigenteCount = invoices.filter(inv => inv && inv.status && inv.status !== "Vigente").length;

    return {
      total,
      vigente,
      conRequisicion,
      sinRequisicionCount,
      sinRequisicionMonto,
      noVigenteCount
    };
  }, [invoiceList]);

  const requisitionStats = useMemo(() => {
    const requisitions = Array.isArray(requisitionList) ? requisitionList : [];
    const recientes = requisitions.slice(0, 5);
    const completas = requisitions.filter(req => req && req.status === "Completo").length;
    const enProceso = requisitions.filter(req => req && req.status && req.status !== "Completo").length;
    return { recientes, completas, enProceso };
  }, [requisitionList]);

  const alerts = useMemo(() => {
    const items = [];
    if (!activeCityhall) {
      items.push({
        color: "warning",
        text: "Selecciona un ayuntamiento para ver información y generar requisiciones."
      });
    }
    if (invoiceStats.sinRequisicionCount > 0) {
      items.push({
        color: "info",
        text: `Tienes ${invoiceStats.sinRequisicionCount} facturas vigentes sin requisición.`
      });
    }
    if (invoiceStats.noVigenteCount > 0) {
      items.push({
        color: "danger",
        text: `Hay ${invoiceStats.noVigenteCount} facturas con estatus distinto a "Vigente".`
      });
    }
    if (invoiceStats.total === 0) {
      items.push({
        color: "secondary",
        text: "Aún no hay facturas cargadas. Sube un archivo para iniciar el flujo."
      });
    }
    return items.slice(0, 5);
  }, [activeCityhall, invoiceStats]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={props.t("Dashboards")}
            breadcrumbItem={props.t("Dashboard")}
          />
          <Row className="mb-3">
            <Col xs="12">
              <Card>
                <CardBody className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div>
                    <CardTitle className="mb-1">Acciones rápidas</CardTitle>
                    <div className="text-muted">
                      {activeCityhall && activeCityhall.name ? `Ayuntamiento: ${activeCityhall.name}` : "Sin ayuntamiento seleccionado"}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button color="primary" tag={Link} to="/invoices">
                      Ir a Facturas
                    </Button>
                    <Button color="success" tag={Link} to="/requisitions">
                      Ir a Requisiciones
                    </Button>
                    <Button color="secondary" tag={Link} to="/config">
                      Configuración
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xl="3" md="6">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="text-muted">Facturas</div>
                      <div className="h4 mb-0">{invoiceStats.total}</div>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-20">
                        <i className="bx bx-receipt" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-muted">
                    Vigentes: <span className="fw-semibold text-dark">{invoiceStats.vigente}</span>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="3" md="6">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="text-muted">Sin requisición</div>
                      <div className="h4 mb-0">{invoiceStats.sinRequisicionCount}</div>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title rounded-circle bg-warning bg-soft text-warning font-size-20">
                        <i className="bx bx-error-circle" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-muted">
                    Monto: <span className="fw-semibold text-dark">{formatCurrency(invoiceStats.sinRequisicionMonto)}</span>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="3" md="6">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="text-muted">Requisiciones</div>
                      <div className="h4 mb-0">{typeof totalRequisitions === "number" ? totalRequisitions : 0}</div>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title rounded-circle bg-success bg-soft text-success font-size-20">
                        <i className="bx bx-file" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-muted">
                    Completas: <span className="fw-semibold text-dark">{requisitionStats.completas}</span>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="3" md="6">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="text-muted">En proceso</div>
                      <div className="h4 mb-0">{requisitionStats.enProceso}</div>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title rounded-circle bg-info bg-soft text-info font-size-20">
                        <i className="bx bx-time" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-muted">
                    Revisión: <span className="fw-semibold text-dark">Documentos / firmas</span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg="7">
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <CardTitle className="mb-0">Requisiciones recientes</CardTitle>
                    <Button color="link" tag={Link} to="/requisitions" className="p-0">
                      Ver todas
                    </Button>
                  </div>
                  <ListGroup flush>
                    {requisitionStats.recientes.length > 0 ? (
                      requisitionStats.recientes.map(req => (
                        <ListGroupItem
                          key={req && req.id ? req.id : `${req && req.created_at_f ? req.created_at_f : "na"}-${req && req.status ? req.status : "na"}`}
                          className="d-flex align-items-center justify-content-between"
                          tag={Link}
                          to="/requisitions"
                          action
                        >
                          <div>
                            <div className="fw-semibold">Requisición #{req && req.id ? req.id : ""}</div>
                            <div className="text-muted">{(req && req.created_at_f) || ""}</div>
                          </div>
                          <Badge color={req && req.status === "Completo" ? "success" : "warning"}>
                            {(req && req.status) || "Sin estatus"}
                          </Badge>
                        </ListGroupItem>
                      ))
                    ) : (
                      <ListGroupItem className="text-muted">No hay requisiciones para mostrar.</ListGroupItem>
                    )}
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>
            <Col lg="5">
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">Alertas y sugerencias</CardTitle>
                  <ListGroup flush>
                    {alerts.length > 0 ? (
                      alerts.map((item, idx) => (
                        <ListGroupItem
                          key={`${item.color}-${idx}`}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <span>{item.text}</span>
                          <Badge color={item.color}>{item.color}</Badge>
                        </ListGroupItem>
                      ))
                    ) : (
                      <ListGroupItem className="text-muted">Sin alertas.</ListGroupItem>
                    )}
                  </ListGroup>
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <Button color="primary" outline tag={Link} to="/invoices">
                      Subir facturas
                    </Button>
                    <Button color="success" outline tag={Link} to="/requisitions">
                      Continuar requisición
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};


export default withTranslation()(Dashboard);
